
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CampaignFormData } from "@/components/campaigns/modal/CampaignFormSchema";

export const usePublishCampaign = (userId?: string) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (campaignData: CampaignFormData) => {
      console.log('🔥 [PUBLISH_CAMPAIGN] Starting campaign publish process');
      
      // Usar auth.uid() diretamente para garantir que temos o userId atual
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error('🚨 [PUBLISH_CAMPAIGN] Auth error:', authError);
        throw new Error('Erro de autenticação: ' + authError.message);
      }
      
      if (!user) {
        console.error('🚨 [PUBLISH_CAMPAIGN] User not authenticated');
        throw new Error('Usuário não autenticado');
      }
      
      const userIdAuth = user.id;
      console.log('✅ [PUBLISH_CAMPAIGN] User authenticated:', userIdAuth);
      console.log('🔥 [PUBLISH_CAMPAIGN] Campaign data:', campaignData);

      // Buscar store_id com query mais específica
      console.log('🔍 [PUBLISH_CAMPAIGN] Fetching store_id for user:', userIdAuth);
      
      const { data: tokenData, error: tokenError } = await supabase
        .from('nuvemshop_tokens')
        .select('store_id, is_active, created_at')
        .eq('user_id', userIdAuth)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      console.log('🔍 [PUBLISH_CAMPAIGN] Token query result:', { 
        tokenData, 
        tokenError,
        storeId: tokenData?.store_id
      });

      let storeId = null;

      if (tokenError) {
        if (tokenError.code === 'PGRST116') {
          console.warn('⚠️ [PUBLISH_CAMPAIGN] No active Nuvemshop tokens found');
          toast.warning('Nenhum token Nuvemshop ativo encontrado. Campanha será publicada sem integração.');
        } else {
          console.error('🚨 [PUBLISH_CAMPAIGN] Error fetching store_id:', tokenError);
          toast.error('Erro ao verificar integração Nuvemshop: ' + tokenError.message);
        }
      } else if (tokenData && tokenData.store_id) {
        // FORÇAR CAST EXPLÍCITO PARA STRING
        storeId = String(tokenData.store_id).toString();
        console.log('✅ [PUBLISH_CAMPAIGN] Store ID found and converted to string:', storeId);
        console.log('🔍 [PUBLISH_CAMPAIGN] Store ID type:', typeof storeId);
        console.log('🔍 [PUBLISH_CAMPAIGN] Store ID length:', storeId.length);
        console.log('🔍 [PUBLISH_CAMPAIGN] Store ID value check:', storeId === '' ? 'EMPTY' : 'NOT EMPTY');
        
        // VALIDAÇÃO RIGOROSA
        if (!storeId || storeId === 'null' || storeId === 'undefined' || storeId.trim() === '') {
          console.error('🚨 [PUBLISH_CAMPAIGN] Store ID is invalid after conversion:', storeId);
          storeId = null;
          toast.warning('Store ID inválido. Campanha será publicada sem integração.');
        } else {
          toast.success('Integração Nuvemshop detectada - campanha será publicada com integração ativa.');
        }
      } else {
        console.warn('⚠️ [PUBLISH_CAMPAIGN] Token found but no store_id');
        toast.warning('Token Nuvemshop encontrado mas sem store_id. Campanha será publicada sem integração.');
      }

      // VALIDAÇÃO FINAL ANTES DO INSERT
      if (storeId) {
        console.log('🔍 [PUBLISH_CAMPAIGN] Final store_id validation:');
        console.log('  - Value:', storeId);
        console.log('  - Type:', typeof storeId);
        console.log('  - Length:', storeId.length);
        console.log('  - Is string?:', typeof storeId === 'string');
        console.log('  - Is empty?:', storeId === '');
        console.log('  - Is null?:', storeId === null);
        console.log('  - Is undefined?:', storeId === undefined);
      }

      console.log('Publishing campaign with data:', campaignData);

      // Determine status based on start date
      const startDate = new Date(campaignData.start_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      startDate.setHours(0, 0, 0, 0);
      
      const status = startDate <= today ? 'active' : 'scheduled';

      // Criar objeto de dados da campanha incluindo store_id
      const campaignInsertData = {
        user_id: userIdAuth,
        name: campaignData.name,
        type: campaignData.type,
        product: campaignData.product,
        value: campaignData.value,
        start_date: campaignData.start_date.toISOString().split('T')[0],
        end_date: campaignData.end_date.toISOString().split('T')[0],
        description: campaignData.description,
        max_coupons_per_customer: campaignData.max_coupons_per_customer,
        delay_days: campaignData.delay_days,
        store_id: storeId, // INCLUINDO O STORE_ID AQUI
        // Campos de cashback customizado
        use_custom_cashback: campaignData.use_custom_cashback || false,
        custom_cashback_percentage: campaignData.custom_cashback_percentage,
        custom_cashback_multiplier: campaignData.custom_cashback_multiplier,
        custom_cashback_expiration_days: campaignData.custom_cashback_expiration_days,
        status
      };

      console.log('💾 [PUBLISH_CAMPAIGN] Inserting campaign data:', campaignInsertData);
      console.log('💾 [PUBLISH_CAMPAIGN] Store ID being inserted (final check):', campaignInsertData.store_id);
      console.log('💾 [PUBLISH_CAMPAIGN] Store ID type (final check):', typeof campaignInsertData.store_id);

      // TESTE: Inserir com logs ainda mais detalhados
      console.log('🚀 [PUBLISH_CAMPAIGN] About to execute insert...');

      const { data, error } = await supabase
        .from('campaigns')
        .insert(campaignInsertData)
        .select()
        .single();

      console.log('📊 [PUBLISH_CAMPAIGN] Insert result:', { data, error });

      if (error) {
        console.error('🚨 [PUBLISH_CAMPAIGN] Error publishing campaign:', error);
        console.error('🚨 [PUBLISH_CAMPAIGN] Error details:', JSON.stringify(error, null, 2));
        throw error;
      }

      console.log('✅ [PUBLISH_CAMPAIGN] Campaign published successfully:', data);
      console.log('✅ [PUBLISH_CAMPAIGN] Verifying store_id in published campaign:', data.store_id);
      console.log('✅ [PUBLISH_CAMPAIGN] Published campaign store_id type:', typeof data.store_id);
      
      // Verificar se o store_id foi salvo corretamente
      if (storeId && data.store_id !== storeId) {
        console.error('🚨 [PUBLISH_CAMPAIGN] Store ID mismatch after publication:', {
          expected: storeId,
          actual: data.store_id,
          expectedType: typeof storeId,
          actualType: typeof data.store_id
        });
        toast.error('Erro: Store ID não foi salvo corretamente');
      } else if (storeId && data.store_id === storeId) {
        console.log('✅ [PUBLISH_CAMPAIGN] Store ID correctly saved:', data.store_id);
      } else if (!storeId && !data.store_id) {
        console.log('✅ [PUBLISH_CAMPAIGN] No store_id expected and none saved (correct behavior)');
      }

      return data;
    },
    onSuccess: (data) => {
      console.log('🎉 [PUBLISH_CAMPAIGN] Campaign publication success:', data);
      queryClient.invalidateQueries({ queryKey: ['campaigns', userId] });
      
      const statusMessage = data.status === 'active' ? 'ativada' : 'agendada';
      const integrationMessage = data.store_id 
        ? `Integração Nuvemshop ativa (Store: ${data.store_id})` 
        : 'Sem integração Nuvemshop.';
      
      toast.success(`Campanha ${statusMessage} com sucesso! ${integrationMessage}`);
    },
    onError: (error: any) => {
      console.error('💥 [PUBLISH_CAMPAIGN] Failed to publish campaign:', error);
      toast.error("Erro ao publicar campanha: " + (error.message || 'Erro desconhecido'));
    },
  });
};
